import type { PostHog } from 'posthog-js';

// Sole importer of posthog-js - a static import anywhere else puts the SDK
// back into every page's first load (docs/tasks/aceb-214-js-hydration-audit.md).

const IDLE_TIMEOUT_MS = 3000;
const NO_IDLE_FALLBACK_MS = 2000;

let instance: PostHog | null = null;
let loadPromise: Promise<PostHog | null> | null = null;

// Consent chosen before the SDK finished loading; replayed at init.
let pendingOptIn: boolean | null = null;

export function loadPosthog(): Promise<PostHog | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  if (!loadPromise) {
    loadPromise = import('posthog-js')
      .then(({ default: posthog }) => {
        if (process.env.NEXT_PUBLIC_POSTHOG_KEY) {
          posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
            api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
            defaults: '2026-01-30',
            cookieless_mode: 'on_reject',
            // PostHog's own web-vitals module duplicates the first-party RUM
            // (ACEB-219, docs/features/web-vitals-rum.md). Remove to re-enable.
            capture_performance: { web_vitals: false },
          });
        }
        instance = posthog;
        if (pendingOptIn !== null) applyOptIn(posthog, pendingOptIn);
        return posthog;
      })
      .catch(() => {
        loadPromise = null; // failed fetch: let a later call retry
        return null;
      });
  }
  return loadPromise;
}

export function loadPosthogWhenIdle(): void {
  if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
  const load = () => void loadPosthog();
  const idle = (window as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
  if (idle) {
    idle(load, { timeout: IDLE_TIMEOUT_MS });
  } else {
    setTimeout(load, NO_IDLE_FALLBACK_MS);
  }
}

function applyOptIn(posthog: PostHog, optIn: boolean): void {
  try {
    if (optIn) posthog.opt_in_capturing();
    else posthog.opt_out_capturing();
  } catch {
    // posthog may not be fully initialized
  }
}

/** Never triggers the SDK download; a pre-load choice is replayed at init. */
export function setPosthogAnalyticsConsent(optIn: boolean): void {
  pendingOptIn = optIn;
  if (instance) applyOptIn(instance, optIn);
}

/** Null until the idle-time load completes. */
export function getPosthog(): PostHog | null {
  return instance;
}
